import React, { useEffect, useState } from "react"
import { useIsMobile } from "@/hooks/use-mobile"
import { Lead } from "./leads/types/LeadTypes"
import MobileMetricsGrid from "./metrics/MobileMetricsGrid"
import DesktopMetricsGrid from "./metrics/DesktopMetricsGrid"
import { calculateMetrics } from "./metrics/metricsUtils"
import { TimeFrameOption, DEFAULT_TIMEFRAME } from "./leads/utils/dateFilters"
import { fetchLeadsForBusiness } from "@/utils/firestore/fetchLeads"

interface MetricsOverviewProps {
  businessId?: string
  leads?: Lead[]
  timeFrame?: TimeFrameOption | string
}

const MetricsOverview: React.FC<MetricsOverviewProps> = ({
  businessId = "1",
  leads: propLeads,
  timeFrame = DEFAULT_TIMEFRAME,
}) => {
  const isMobile = useIsMobile()
  const [leads, setLeads] = useState<Lead[]>(propLeads || [])

  // Use leads from props if provided, otherwise fetch from Firestore
  useEffect(() => {
    if (propLeads) {
      setLeads(propLeads)
    } else if (businessId) {
      const loadLeads = async () => {
        try {
          const fetchedLeads = await fetchLeadsForBusiness(businessId)
          setLeads(fetchedLeads)
        } catch (error) {
          console.error("Error fetching leads for metrics:", error)
        }
      }

      loadLeads()
    }
  }, [businessId, propLeads])

  // State to store metrics
  const [metrics, setMetrics] = useState({
    nonDisqualifiedLeads: 0,
    totalSpent: 0,
    costPerLead: 0,
    callsAnsweredPercentage: 0,
    closedDealsCount: 0,
    grossProfit: 0,
    roi: 0,
    closeRate: 0,
    phoneCallLeads: [],
    leadsToBreakeven: 0,
  })

  // Calculate metrics when leads or timeFrame changes
  useEffect(() => {
    const calculateMetricsData = async () => {
      if (leads.length > 0 || businessId) {
        try {
          const metricsData = await calculateMetrics(
            leads,
            businessId,
            timeFrame
          )
          console.log(metricsData)
          setMetrics(metricsData)
        } catch (error) {
          console.error("Error calculating metrics:", error)
        }
      }
    }

    calculateMetricsData()
  }, [leads, businessId, timeFrame])

  // Destructure metrics for easier access
  const {
    nonDisqualifiedLeads,
    totalSpent,
    costPerLead,
    callsAnsweredPercentage,
    closedDealsCount,
    grossProfit,
    roi,
    closeRate,
  } = metrics

  if (isMobile) {
    return (
      <MobileMetricsGrid
        nonDisqualifiedLeads={nonDisqualifiedLeads}
        totalSpent={totalSpent}
        costPerLead={costPerLead}
        callsAnsweredPercentage={callsAnsweredPercentage}
        closedDealsCount={closedDealsCount}
        grossProfit={grossProfit}
        roi={roi}
        closeRate={closeRate}
        timeFrame={timeFrame}
      />
    )
  }

  return (
    <DesktopMetricsGrid
      nonDisqualifiedLeads={nonDisqualifiedLeads}
      totalSpent={totalSpent}
      costPerLead={costPerLead}
      callsAnsweredPercentage={callsAnsweredPercentage}
      closedDealsCount={closedDealsCount}
      grossProfit={grossProfit}
      roi={roi}
      closeRate={closeRate}
      timeFrame={timeFrame}
    />
  )
}

export default MetricsOverview
